import { useEffect, useRef, useState, type FormEvent } from "react";
import { LuGlobe, LuMessageCircleOff, LuSwords, LuUserPlus, LuUsers } from "react-icons/lu";
import { ApiError, get, patch, post } from "../api";
import { Avatar } from "../components/Avatar";
import { refreshFriendSummary } from "../components/friendbadge";
import { HeroBubble, PageHero } from "../components/PageHero";
import { ErrorBox, Field, GAME_META, Spinner } from "../components/ui";
import { navigate } from "../router";
import { SUBJECT_IDS } from "../../shared/constants";
import { useApp, useLoad } from "../store";

interface Prof { username: string; level: number; title?: string | null; avatar: Record<string, any> }
interface Friend extends Prof { online: boolean; where: "site" | "verden" | null }
interface Invite { id: number; from: Prof; kind: "match" | "world"; game: string | null; code: string | null; at: number }
interface Payload { friends: Friend[]; incoming: (Prof & { at: number })[]; outgoing: { username: string; at: number }[]; invites: Invite[]; blocked: string[]; allowRequests: boolean; max: number }

/** the games a friend can be challenged to (two or more players online) */
const CHALLENGES = ["kryds", "fire", "quizduel", "ludo", "yatzy", "skak", "kortkamp", "slagskibe"] as const;

export function Friends() {
  const { me, toast } = useApp();
  const { data, error, loading, reload } = useLoad(() => get<Payload>("/api/friends"), []);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; const id = setInterval(() => { if (!document.hidden) void reload(); }, 15_000); return () => { alive.current = false; clearInterval(id); }; }, [reload]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await fn(); if (ok) toast(ok, "ok"); await reload(); void refreshFriendSummary(); } catch (e) { toast(e instanceof ApiError ? e.message : "Noget gik galt. Prøv igen.", "error"); }
    if (alive.current) setBusy(false);
  };
  const add = (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    void run(async () => {
      const r = await post<{ status: "sent" | "accepted"; username: string }>("/api/friends/request", { username: n });
      toast(r.status === "accepted" ? `Du og ${r.username} er nu venner!` : `Anmodningen er sendt til ${r.username}`, "ok");
      setName("");
    });
  };
  const challenge = (f: Friend, game: (typeof CHALLENGES)[number]) =>
    run(async () => {
      const m = await post<{ id: string; code: string | null }>("/api/matches", { game, subjects: SUBJECT_IDS, grade: me?.grade ?? 5, difficulty: "mix", mode: "lobby" });
      if (m.code) await post("/api/friends/invite", { username: f.username, kind: "match", game, code: m.code });
      toast(`${f.username} har fået en invitation. Vent i venteværelset.`, "ok");
      navigate(`/kamp/${m.id}`);
    });
  const toWorld = (f: Friend) =>
    run(async () => {
      if (f.where !== "verden") await post("/api/friends/invite", { username: f.username, kind: "world" }).catch(() => {});
      navigate(`/verden?ven=${encodeURIComponent(f.username)}`);
    });
  const answer = (inv: Invite, accept: boolean) =>
    run(async () => {
      const r = await post<{ kind?: "match" | "world"; code?: string | null; from?: string | null }>("/api/friends/invite/answer", { id: inv.id, accept });
      if (!accept) return;
      if (r.kind === "match" && r.code) {
        const j = await post<{ id: string }>("/api/matches/join", { code: r.code });
        navigate(`/kamp/${j.id}`);
      } else if (r.kind === "world") navigate(`/verden?ven=${encodeURIComponent(inv.from.username)}`);
    });

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  const d = data!;
  const onlineCount = d.friends.filter((f) => f.online).length;
  return (
    <div className="stack rise friends-page">
      <PageHero tone="friends" title="Venner" icon={<LuUsers size={44} />} sub="Find dem, du kender, og spil sammen – i spillene og i Fri Verden.">
        <HeroBubble icon={<LuUsers aria-hidden="true" size={22} />} tone="blue" value={d.friends.length} label={d.friends.length === 1 ? "ven" : "venner"} />
        <HeroBubble icon={<LuGlobe aria-hidden="true" size={22} />} tone="pink" value={onlineCount} label="online nu" />
      </PageHero>

      {d.invites.length > 0 && (
        <section className="fr-section" aria-label="Invitationer">
          {d.invites.map((inv) => (
            <div key={inv.id} className="card fr-invite" role="status">
              <Avatar equipped={inv.from.avatar} size={56} label="" />
              <div className="fr-who">
                <b>{inv.from.username}</b>
                <span>{inv.kind === "world" ? "inviterer dig ind i Fri Verden" : `vil spille ${GAME_META[inv.game ?? ""]?.name ?? "et spil"} med dig`}</span>
              </div>
              <button className="btn lime" disabled={busy} onClick={() => answer(inv, true)}>{inv.kind === "world" ? "Kom med" : "Spil med"}</button>
              <button className="btn ghost" disabled={busy} onClick={() => answer(inv, false)}>Nej tak</button>
            </div>
          ))}
        </section>
      )}

      <form className="card fr-add" onSubmit={add}>
        <span className="fr-add-ico" aria-hidden="true"><LuUserPlus size={26} /></span>
        <Field label="Find en ven" hint="Skriv det præcise brugernavn – så får din ven en anmodning">
          <input value={name} onChange={(e: any) => setName(e.target.value)} maxLength={40} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="Din vens brugernavn" />
        </Field>
        <button className="btn lime" disabled={busy || !name.trim()}>Send anmodning</button>
      </form>

      {d.incoming.length > 0 && (
        <section className="fr-section" aria-label="Venneanmodninger">
          <h2 className="fr-h">Vil være din ven ({d.incoming.length})</h2>
          {d.incoming.map((r) => (
            <div key={r.username} className="card fr-invite">
              <Avatar equipped={r.avatar} size={56} label="" />
              <div className="fr-who"><b>{r.username}</b><span>Niveau {r.level}</span></div>
              <button className="btn lime" disabled={busy} onClick={() => run(() => post("/api/friends/respond", { username: r.username, accept: true }), `Du og ${r.username} er nu venner!`)}>Ja, bliv venner</button>
              <button className="btn ghost" disabled={busy} onClick={() => run(() => post("/api/friends/respond", { username: r.username, accept: false }))}>Nej tak</button>
            </div>
          ))}
        </section>
      )}

      <section className="fr-section" aria-label="Mine venner">
        <h2 className="fr-h">Mine venner</h2>
        {d.friends.length === 0 ? (
          <div className="emptycard">
            <span className="emptyico" aria-hidden="true">🤝</span>
            <div><b>Du har ikke nogen venner her endnu.</b><p className="muted">Skriv en vens brugernavn ovenfor – så kan I spille sammen.</p></div>
          </div>
        ) : (
          <div className="fr-grid">
            {d.friends.map((f) => (
              <div key={f.username} className={`card fr-card${f.online ? " on" : ""}`}>
                <div className="fr-top">
                  <Avatar equipped={f.avatar} size={72} label="" />
                  <div className="fr-who">
                    <b>{f.username}</b>
                    <span className={`fr-dot ${f.online ? "on" : ""}`}>{f.online ? (f.where === "verden" ? "I Fri Verden nu" : "Online") : "Ikke online"}</span>
                    <small className="muted">Niveau {f.level}{f.title ? ` · ${f.title}` : ""}</small>
                  </div>
                </div>
                <div className="fr-actions">
                  <button className="btn" disabled={busy} onClick={() => toWorld(f)}><LuGlobe aria-hidden="true" size={16} /> {f.where === "verden" ? "Besøg i verden" : "Inviter til verden"}</button>
                  <button className="btn ghost" disabled={busy} aria-expanded={open === f.username} onClick={() => setOpen(open === f.username ? null : f.username)}><LuSwords aria-hidden="true" size={16} /> Udfordr</button>
                </div>
                {open === f.username && (
                  <div className="fr-games" role="group" aria-label={`Vælg et spil mod ${f.username}`}>
                    {CHALLENGES.map((g) => <button key={g} className="sfilter" disabled={busy} onClick={() => challenge(f, g)}>{GAME_META[g]?.name ?? g}</button>)}
                  </div>
                )}
                <div className="fr-more">
                  <button className="linkbtn" disabled={busy} onClick={() => { if (confirm(`Vil du fjerne ${f.username} som ven?`)) void run(() => post("/api/friends/remove", { username: f.username }), `${f.username} er ikke din ven mere`); }}>Fjern ven</button>
                  <button className="linkbtn" disabled={busy} onClick={() => { if (confirm(`Vil du blokere ${f.username}? Så kan ${f.username} ikke sende dig anmodninger.`)) void run(() => post("/api/friends/block", { username: f.username }), `${f.username} er blokeret`); }}>Bloker</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {d.outgoing.length > 0 && (
        <section className="fr-section" aria-label="Sendte anmodninger">
          <h2 className="fr-h">Venter på svar</h2>
          <ul className="fr-wait">
            {d.outgoing.map((o) => (
              <li key={o.username}><span><b>{o.username}</b> har ikke svaret endnu</span><button className="linkbtn" disabled={busy} onClick={() => run(() => post("/api/friends/remove", { username: o.username }))}>Træk tilbage</button></li>
            ))}
          </ul>
        </section>
      )}

      <section className="card fr-safe" aria-label="Tryghed">
        <h2 className="fr-h"><LuMessageCircleOff aria-hidden="true" size={20} /> Tryghed</h2>
        <p>Her kan man <strong>ikke skrive til hinanden</strong>. Man kan altid sige nej tak, fjerne en ven eller blokere.</p>
        <label className="switch-row">
          <input type="checkbox" checked={d.allowRequests} disabled={busy} onChange={(e: any) => run(() => patch("/api/friends/settings", { allowRequests: e.target.checked }), e.target.checked ? "Andre må sende dig anmodninger" : "Ingen kan sende dig anmodninger nu")} />
          <span>Andre må sende mig venneanmodninger</span>
        </label>
        {d.blocked.length > 0 && (
          <p className="muted">Blokeret: {d.blocked.map((b) => (
            <span key={b} className="fr-blk">{b} <button className="linkbtn" disabled={busy} onClick={() => run(() => post("/api/friends/unblock", { username: b }))}>fjern blokering</button></span>
          ))}</p>
        )}
      </section>
    </div>
  );
}
